import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Medal } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxoYW5keW1hbiUyMGZpeGluZyUyMHRvb2xzfGVufDB8fHx8MTc5MTYzOTYxNXww&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "YOUR HOME.", cls: "text-foreground" },
  { text: "OUR MISSION:", cls: "text-khaki" },
  { text: "FIXED RIGHT.", cls: "text-foreground" },
];

const MaskedLine = ({ children, delay, cls }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block ${cls}`}
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({
      x: ((e.clientY - r.top) / r.height - 0.5) * -8,
      y: ((e.clientX - r.left) / r.width - 0.5) * 10,
    });
  };

  return (
    <section ref={ref} className="relative min-h-screen blueprint-grid pt-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-0 min-h-[calc(100vh-4rem)]">
        {/* left: kinetic type */}
        <div className="lg:col-span-7 flex flex-col justify-center py-20 lg:pr-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="overline-tactical text-coyote mb-8"
            data-testid="hero-overline"
          >
            OP-FIX // 01 // DEPLOYMENT READY <span className="blink text-alert">_</span>
          </motion.p>
          <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl xl:text-8xl tracking-tight" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <MaskedLine key={l.text} delay={0.35 + i * 0.18} cls={l.cls}>
                {l.text}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 max-w-md text-sm sm:text-base text-muted-foreground leading-relaxed"
            data-testid="hero-subcopy"
          >
            OPERATION FIX-IT deploys elite handyman units for installations,
            repairs, and assembly. One call. One crew. Mission complete.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#deploy"
              data-testid="hero-deploy-cta"
              className="dogtag bg-alert text-white font-bold text-sm tracking-[0.2em] px-8 py-4 hover:bg-khaki hover:text-obsidian transition-colors duration-300"
            >
              &gt;&gt; REQUEST A SERVICE CALL
            </a>
            <a
              href="#services"
              data-testid="hero-services-cta"
              className="border border-olive-deep text-khaki text-sm tracking-[0.2em] px-8 py-4 hover:bg-olive-deep transition-colors duration-300"
            >
              VIEW OPERATIONS
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-14 grid grid-cols-3 max-w-md border border-olive-deep divide-x divide-olive-deep"
            data-testid="hero-stats"
          >
            {[["25+", "OPERATIONS"], ["24HR", "RESPONSE"], ["100%", "MISSION READY"]].map(([v, l]) => (
              <div key={l} className="p-4">
                <div className="font-display text-2xl text-khaki">{v}</div>
                <div className="text-[9px] tracking-[0.25em] text-coyote mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.65, duration: 0.8 }}
            className="mt-6 inline-flex items-center gap-2.5 border border-khaki/60 bg-olive-deep/40 px-4 py-2.5 self-start dogtag"
            data-testid="hero-veteran-badge"
          >
            <Medal className="w-4 h-4 text-khaki" strokeWidth={2} />
            <span className="text-[10px] tracking-[0.3em] text-khaki font-bold">VETERAN OWNED &amp; OPERATED</span>
          </motion.div>
        </div>

        {/* right: framed imagery with parallax + tilt */}
        <div className="lg:col-span-5 relative hidden lg:flex items-center">
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full"
            style={{ perspective: 900 }}
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
          >
            <motion.div
              animate={{ rotateX: tilt.x, rotateY: tilt.y }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              className="relative border border-olive-deep dogtag overflow-hidden"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Precision power drill, ready for deployment"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-[560px] object-cover tactical-img"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-olive/20 mix-blend-multiply pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 flex justify-between items-center bg-obsidian/90 border-t border-olive-deep px-4 py-2">
                <span className="text-[10px] tracking-[0.3em] text-coyote">FIELD UNIT 01 // TOOLS READY</span>
                <span className="w-2 h-2 bg-alert animate-pulse" />
              </div>
            </motion.div>
            <div className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-khaki pointer-events-none" />
            <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-khaki pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
